import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { DEFAULT_PER_PAGE } from "@/constants";
import { useRedeemedCoupons } from "@/hooks/coupon/useRedeemedCoupons";
import { useHandleError } from "@/hooks/useHandleError";
import type { RedeemedCouponCode } from "@/types/Coupon";
import { format } from "date-fns";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";

export const RedeemedCoupons = () => {
  const {
    data: pages = [],
    size,
    setSize,
    isLoading,
  } = useRedeemedCoupons(() => {
    useHandleError("クーポン履歴の取得に失敗しました");
  });

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  useEffect(() => {
    if (inView) {
      setSize((prevSize) => prevSize + 1);
    }
  }, [inView, setSize]);

  const couponCodes = pages.flat();
  const isReachedEnd = couponCodes.length === 0 || pages[pages.length - 1].length < DEFAULT_PER_PAGE;

  return (
    <div>
      <p className="font-bold px-1 text-xl">クーポン使用履歴</p>

      <Separator className="my-2 bg-disabled" />

      {isLoading && size === 1 && (
        <div className="flex flex-col gap-5 px-1 mt-5">
          {Array.from({ length: DEFAULT_PER_PAGE }).map((_, index) => (
            <CouponHistoryItemSkeleton key={index} />
          ))}
        </div>
      )}

      {!isLoading && (
        <div className="px-1 mt-5">
          {couponCodes?.map((couponCode: RedeemedCouponCode) => (
            <CouponHistoryItem key={couponCode.id} couponCode={couponCode} />
          ))}
        </div>
      )}

      {!isLoading && !isReachedEnd && <div ref={ref} />}
    </div>
  );
};

const CouponHistoryItemSkeleton = () => {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="w-1/3 h-4" />
      <Skeleton className="w-full h-4" />
    </div>
  );
};

const CouponHistoryItem = ({ couponCode }: { couponCode: RedeemedCouponCode }) => {
  const amount = couponCode.reward.amount;
  const message = `${amount}ポイント獲得：${couponCode.name}のクーポン使用されました。`;
  const [date, time] = format(new Date(couponCode.redeemedAt), "yyyy年M月d日 HH時mm分").split(" ");

  return (
    <div className="py-2">
      <h6 className="text-gray flex gap-2 text-sm">
        <span>{date}</span>
        <span>{time}</span>
      </h6>

      <p className="mt-1 underline text-base">{message}</p>
    </div>
  );
};

export default RedeemedCoupons;
